import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AttachmentStrongIcon,
  DeleteStrongIcon,
  EditStrongIcon,
  FrameImageStrongIcon,
  VideoStrongIcon,
} from 'src/monet/icons';
import { IconButton } from 'src/common/icon-button';
import { Nullable } from 'src/types/nullable.type';
import { StepEditorWarningPanel } from '../../warning-panel';
import { functionNoop } from 'src/utils/function/noop';

interface StepEditorContentFilePreviewProps {
  file: File;
  fileMeta?: Nullable<string>;
  uploadInProgress?: boolean;
  uploadPercentage?: number;
  titleClassName?: string;
  onDelete?: () => void;
  onEditClick?: () => void;
  onUploadCompleted: (file: File) => void;
  disableEditing?: boolean;
}

export const StepEditorContentFilePreview: FC<StepEditorContentFilePreviewProps> = ({
  file,
  fileMeta = null,
  uploadInProgress = false,
  uploadPercentage = 0,
  titleClassName = 'text-bedrock-p-strong',
  onDelete,
  onEditClick,
  onUploadCompleted,
  disableEditing,
}) => {
  const warningPanelOpenRef = useRef(functionNoop);
  const warningPanelCloseRef = useRef(functionNoop);

  const thumbnail = useMemo(() => {
    if (file.type.startsWith('video')) {
      return <VideoStrongIcon className='w-5 h-5 text-bedrock-brand-text' />;
    } else if (file.type.startsWith('image')) {
      return <FrameImageStrongIcon className='w-5 h-5 text-bedrock-brand-text' />;
    }

    return <AttachmentStrongIcon className='w-5 h-5 text-bedrock-brand-text' />;
  }, [file]);

  const filePreviewParams = useMemo(() => {
    return {
      type: file.type,
      name: file.name,
      thumbnail: thumbnail,
    };
  }, [file, thumbnail]);

  useEffect(() => {
    if (uploadPercentage === 100) {
      onUploadCompleted(file);
    }
  }, [file, uploadPercentage]);

  const meta = useMemo(() => {
    if (uploadInProgress && uploadPercentage) {
      return `Uploading ${uploadPercentage}%`;
    }

    return fileMeta || null;
  }, [uploadInProgress, uploadPercentage, fileMeta]);

  const onWarningPanelConfirmed = useCallback(() => {
    warningPanelCloseRef.current();
    if (onEditClick) {
      onEditClick();
    }
  }, [onEditClick]);

  const onWarningPanelDenied = useCallback(() => {
    warningPanelCloseRef.current();
  }, []);

  return (
    <div className='relative flex items-start justify-between'>
      <div className='relative flex justify-start items-start rounded-md'>
        <div
          className={classNames(
            'flex flex-shrink-0 h-10 w-10 bg-bedrock-brand-primary items-center justify-center min-w-0 relative rounded-lg',
            disableEditing && 'opacity-80'
          )}
        >
          {thumbnail}
        </div>
        <div className='flex flex-col ml-2 min-w-0 flex-grow-0'>
          <div className={classNames('text-bedrock-black', titleClassName)} style={{ wordBreak: 'break-word' }}>
            {filePreviewParams.name}
          </div>
          {meta && <div className='text-bedrock-p-small text-bedrock-dark-gray'>{meta}</div>}
        </div>
      </div>
      {onDelete && (
        <button type='button' className='text-bedrock-black hover:opacity-80' onClick={onDelete}>
          <DeleteStrongIcon />
        </button>
      )}
      {onEditClick && (
        <StepEditorWarningPanel
          onConfirm={onWarningPanelConfirmed}
          openRef={warningPanelOpenRef}
          closeRef={warningPanelCloseRef}
          onDeny={onWarningPanelDenied}
          copy={
            <span className='text-bedrock-p text-bedrock-black'>
              Would you like to change this content to something else? Everything else will remain.
            </span>
          }
          confirmCopy='Yes, change'
          denyCopy='No, keep'
          renderOrigin={() => (
            <IconButton disabled={disableEditing}>
              <EditStrongIcon />
            </IconButton>
          )}
        />
      )}
    </div>
  );
};
